import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { EyeOff, MessageSquareOff, Users, Target, Hourglass, LifeBuoy } from "lucide-react";

const ITEMS = [
  { icon: EyeOff, title: "Keine transparenten Preise", body: "Verstecktes Kleingedrucktes, offene Stundensätze, überraschende Nachforderungen." },
  { icon: MessageSquareOff, title: "Schlechte Kommunikation", body: "Wochenlange Funkstille, wechselnde Ansprechpartner, unklare Zuständigkeiten." },
  { icon: Users, title: "Website bringt keine Kunden", body: "Schön aussehen ist nicht genug. Ohne System bleibt die Website ein digitales Broschüre." },
  { icon: Target, title: "Keine Marketingstrategie", body: "Design ohne Vertriebsplan. Traffic ohne Ziel. Ads ohne Messbarkeit." },
  { icon: Hourglass, title: "Lange Wartezeiten", body: "Monate bis zur ersten Vorschau. Der Launch verschiebt sich immer wieder." },
  { icon: LifeBuoy, title: "Keine Betreuung nach dem Launch", body: "Nach der Freigabe verschwindet die Agentur — und mit ihr die Verantwortung." },
];

function Card({ item, i }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-10% 0px" });
  const Icon = item.icon;
  const dir = i % 2 === 0 ? -1 : 1;
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40, x: dir * 20, rotate: dir * -1.2 }}
      animate={inView ? { opacity: 1, y: 0, x: 0, rotate: 0 } : {}}
      transition={{ duration: 0.85, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -6, transition: { duration: 0.4 } }}
      className="group relative bg-white border border-[#E5E5E0] p-8 md:p-10 hover:border-[#C9A961] transition-colors duration-500"
      data-testid={`problem-card-${i}`}
    >
      <div className="flex items-start justify-between mb-8">
        <div className="w-11 h-11 rounded-full border border-[#0A0A0A]/15 flex items-center justify-center group-hover:border-[#C9A961] group-hover:text-[#C9A961] transition-colors duration-500">
          <Icon size={18} strokeWidth={1.4} />
        </div>
        <span className="chapter-num">{String(i + 1).padStart(2, "0")}</span>
      </div>
      <h4 className="font-serif-editorial text-2xl md:text-[26px] leading-tight">{item.title}</h4>
      <p className="mt-4 text-[#0A0A0A]/70 text-[15px] leading-relaxed">{item.body}</p>
    </motion.div>
  );
}

export default function Problem() {
  return (
    <section id="problem" className="bg-[#FAFAF7] py-24 md:py-36 border-t border-black/5">
      <div className="mx-auto max-w-[1440px] px-5 md:px-10">
        <div className="grid grid-cols-12 gap-6 mb-14 md:mb-20">
          <div className="col-span-12 lg:col-span-4 flex items-start gap-4">
            <span className="chapter-num mt-2">01 — Problem</span>
          </div>
          <h2 className="col-span-12 lg:col-span-8 font-serif-editorial text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
            Kommt Ihnen<br />das <em className="italic text-[#C9A961]">bekannt</em> vor?
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {ITEMS.map((it, i) => <Card key={it.title} item={it} i={i} />)}
        </div>
      </div>
    </section>
  );
}
